/**
 * v2 workspace queries — programs, projects, members, chats, notebook,
 * data, memory, invitations, artifact pin/share.
 *
 * Each query hits the real backend endpoint via RequestBuilder, then
 * falls back to fixture data on 404 (endpoint not yet shipped on this
 * cell) or when the request fails entirely (offline / config issue).
 * This lets v2 ship per-endpoint as the backend lands them, instead of
 * an all-or-nothing flip.
 *
 * To force fixtures across the board (useful for offline development),
 * set FORCE_FIXTURES = true OR pass ?fixtures=force in the URL.
 *
 * To force live calls (skip fixtures even on 404), set the URL param
 * ?fixtures=skip — the query rejects on failure so the caller sees the
 * real error.
 */

import { getConfig } from "../lib/configMgr";
import { RequestBuilder, toJSON } from "./request";
import { NotEntitledError } from "./errors";
import fixtures from "../configs/v2-mock-fixtures.json";

const { ExternalRESTAPI } = getConfig();

// Set to true to force fixture data for every query (dev-only escape hatch).
const FORCE_FIXTURES = false;

const urlMode = () => {
  if (typeof window === "undefined") return null;
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get("fixtures"); // "force" | "skip" | null
  } catch (_) {
    return null;
  }
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fillTemplate = (template, params) =>
  Object.entries(params || {}).reduce(
    (s, [k, v]) => s.replace(`{${k}}`, encodeURIComponent(String(v ?? ""))),
    template || "",
  );

const projectKey = (programId, projectId) => `${programId}/${projectId}`;

const shouldUseFixturesFirst = () =>
  FORCE_FIXTURES || urlMode() === "force";

const fixturesAllowed = () => urlMode() !== "skip";

/**
 * Try a real fetch; fall back to a fixture provider on 404 or network
 * failure (when fixtures are allowed).
 *
 *  - urlTemplate: string with `{param}` placeholders, e.g.
 *    "/api/programs/{programId}/projects".
 *  - params: object to interpolate into the template.
 *  - opts:
 *      method (default "GET")
 *      body: JSON-serializable
 *      auth: authData
 *      fixture: () => any (returns the fixture payload).
 *      label: short string for log lines.
 */
const fetchOrFixture = async (urlTemplate, params, opts = {}) => {
  const { method = "GET", body = null, auth = null, fixture, label = urlTemplate } = opts;

  // Forced fixtures: return immediately if a fixture is provided.
  if (shouldUseFixturesFirst() && typeof fixture === "function") {
    await wait(15);
    return fixture();
  }

  if (!urlTemplate) {
    if (fixturesAllowed() && typeof fixture === "function") {
      // info, not warn — fixture fallback is the intentional pre-ship behavior
      // for endpoints that aren't on the cell yet.
      console.info(`[v2 query] no endpoint configured for ${label}; using fixture`);
      return fixture();
    }
    throw new Error(`[v2 query] no endpoint configured for ${label}`);
  }

  const url = fillTemplate(urlTemplate, params);
  const req = new RequestBuilder(url);
  if (auth) req.withAuthorization(auth);
  if (method === "POST") req.asPOST(body == null ? null : JSON.stringify(body)).withJSONContentType();
  else if (method === "PATCH") req.asPATCH(body == null ? null : JSON.stringify(body)).withJSONContentType();
  else if (method === "DELETE") req.asDELETE();

  try {
    return await req.fetch().then(toJSON);
  } catch (err) {
    // A 401/403 (RequestBuilder throws NotAuthorized, with no .status set)
    // means the caller is genuinely not entitled to this feature — that's a
    // real, user-meaningful state, not "endpoint not shipped on this cell
    // yet". Don't paper over it with fixture data; let the caller render a
    // distinct "you don't have access" state instead of an empty one.
    if (err?.name === "NotAuthorized") {
      throw new NotEntitledError(label);
    }
    // Fall back to fixtures for any other error if a fixture is provided AND
    // the caller hasn't opted out via ?fixtures=skip. Real 5xx errors that
    // the caller cares about can still be surfaced via the skip flag.
    if (fixturesAllowed() && typeof fixture === "function") {
      const reason = err?.status ? `status ${err.status}` : (err?.name || "network error");
      console.info(`[v2 query] ${label} → ${reason}; using fixture`);
      return fixture();
    }
    throw err;
  }
};

// ─── Programs ──────────────────────────────────────────────────────────

export const listPrograms = (auth) =>
  fetchOrFixture(ExternalRESTAPI.Programs, null, {
    auth,
    label: "GET /api/programs",
    fixture: () => clone(fixtures.programs),
  });

// Mirrors slugify in createProject — keeps the path id-safe and short.
const slugifyProgram = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

export const createProgram = (auth, input) =>
  fetchOrFixture(ExternalRESTAPI.Programs, null, {
    auth,
    method: "POST",
    body: input,
    label: "POST /api/programs",
    fixture: () => {
      const base = slugifyProgram(input?.id || input?.name) || `program-${Date.now().toString(36)}`;
      let id = base;
      let suffix = 2;
      while (fixtures.programs.some((p) => p.id === id)) {
        id = `${base}-${suffix++}`;
      }
      const program = {
        id,
        name: input?.name || id,
        description: input?.description || "",
        projectCount: 0,
        memberCount: 1,
        lastActiveAt: new Date().toISOString(),
      };
      fixtures.programs = [program, ...fixtures.programs];
      return clone(program);
    },
  });

export const getProgram = async (auth, programId) => {
  const result = await fetchOrFixture(ExternalRESTAPI.Program, { programId }, {
    auth,
    label: `GET /api/programs/${programId}`,
    fixture: () => {
      const p = fixtures.programs.find((x) => x.id === programId);
      if (!p) { const err = new Error(`Program not found: ${programId}`); err.status = 404; throw err; }
      return clone(p);
    },
  });
  return result;
};

/**
 * GET /api/projects/{projectId} — one project, read from its owner's partition.
 * Gated by access.py like the program GET (403 when the caller can't reach it),
 * but a project-only grantee (PLEX-6756) passes here while the program GET
 * refuses them.
 */
export const getProject = (auth, projectId) =>
  fetchOrFixture(ExternalRESTAPI.Project, { projectId }, {
    auth,
    label: `GET /api/projects/${projectId}`,
    fixture: () => {
      const p = Object.values(fixtures.projects || {}).flat().find((x) => x.id === projectId);
      if (!p) { const err = new Error(`Project not found: ${projectId}`); err.status = 404; throw err; }
      return clone(p);
    },
  });

export const listProjectsForProgram = (auth, programId) =>
  fetchOrFixture(ExternalRESTAPI.ProgramProjects, { programId }, {
    auth,
    label: `GET /api/programs/${programId}/projects`,
    fixture: () => clone(fixtures.projects?.[programId] || []),
  });

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

/**
 * PATCH /api/programs/{programId} — rename + edit description.
 * body = { name?, description? }; at least one required.
 */
export const updateProgram = (auth, programId, input) =>
  fetchOrFixture(ExternalRESTAPI.Program, { programId }, {
    auth,
    method: "PATCH",
    body: input,
    label: `PATCH /api/programs/${programId}`,
    fixture: () => {
      const idx = (fixtures.programs || []).findIndex((p) => p.id === programId);
      if (idx === -1) return null;
      const patched = { ...fixtures.programs[idx], ...input };
      fixtures.programs[idx] = patched;
      return clone(patched);
    },
  });

/**
 * DELETE /api/programs/{programId} — soft delete. Returns 204
 * (treated as `{ok: true}` here for the FE).
 */
export const deleteProgram = (auth, programId) =>
  fetchOrFixture(ExternalRESTAPI.Program, { programId }, {
    auth,
    method: "DELETE",
    label: `DELETE /api/programs/${programId}`,
    fixture: () => {
      fixtures.programs = (fixtures.programs || []).filter((p) => p.id !== programId);
      delete fixtures.projects?.[programId];
      return { ok: true };
    },
  });

/**
 * PATCH /api/projects/{projectId} — rename + edit hypothesis + change
 * persona template. body = { name?, hypothesis?, personaTemplate?, programId }.
 * programId is required by the backend validator since v2 projects
 * are nested under programs.
 */
export const updateProject = (auth, projectId, input) =>
  fetchOrFixture(ExternalRESTAPI.Project, { projectId }, {
    auth,
    method: "PATCH",
    body: input,
    label: `PATCH /api/projects/${projectId}`,
    fixture: () => {
      const programId = input?.programId;
      const list = (programId && fixtures.projects?.[programId]) || [];
      const idx = list.findIndex((p) => p.id === projectId);
      if (idx === -1) return null;
      const patched = { ...list[idx], ...input };
      fixtures.projects[programId][idx] = patched;
      return clone(patched);
    },
  });

/**
 * DELETE /api/projects/{projectId} — soft delete.
 */
export const deleteProject = (auth, projectId, programId = null) =>
  fetchOrFixture(ExternalRESTAPI.Project, { projectId }, {
    auth,
    method: "DELETE",
    label: `DELETE /api/projects/${projectId}`,
    fixture: () => {
      if (programId && fixtures.projects?.[programId]) {
        fixtures.projects[programId] = fixtures.projects[programId].filter((p) => p.id !== projectId);
      } else {
        // Search all programs for the project id.
        for (const [pid, list] of Object.entries(fixtures.projects || {})) {
          fixtures.projects[pid] = list.filter((p) => p.id !== projectId);
        }
      }
      return { ok: true };
    },
  });

export const createProject = (auth, programId, input) =>
  fetchOrFixture(ExternalRESTAPI.ProgramProjects, { programId }, {
    auth,
    method: "POST",
    body: input,
    label: `POST /api/programs/${programId}/projects`,
    fixture: () => {
      const existing = fixtures.projects[programId] || [];
      const baseSlug = slugify(input.name) || `project-${Date.now().toString(36)}`;
      let id = baseSlug;
      let suffix = 2;
      while (existing.some((p) => p.id === id)) {
        id = `${baseSlug}-${suffix++}`;
      }
      const project = {
        id,
        name: input.name,
        hypothesis: input.hypothesis || "",
        memberCount: 1,
        chatCount: 0,
        lastActiveAt: new Date().toISOString(),
        personaTemplate: input.personaTemplate || null,
      };
      if (!fixtures.projects[programId]) fixtures.projects[programId] = [];
      fixtures.projects[programId] = [project, ...existing];
      return clone(project);
    },
  });

// ─── Members ──────────────────────────────────────────────────────────

export const listProjectMembers = (auth, programId, projectId) =>
  fetchOrFixture(ExternalRESTAPI.ProjectMembers, { projectId }, {
    auth,
    label: `GET /api/projects/${projectId}/members`,
    fixture: () => clone(fixtures.members?.[projectKey(programId, projectId)] || []),
  });

// ─── Chats ────────────────────────────────────────────────────────────

export const listChatsForProject = (auth, programId, projectId) =>
  fetchOrFixture(ExternalRESTAPI.ProjectChats, { projectId }, {
    auth,
    label: `GET /api/projects/${projectId}/chats`,
    fixture: () => clone(fixtures.chatsByProject?.[projectKey(programId, projectId)] || []),
  });

// ─── Recent chats ─────────────────────────────────────────────────────

// recentChats is derived; no real endpoint yet — falls back to fixture.
export const listRecentChats = async (auth, { programId } = {}) => {
  const result = await fetchOrFixture(null, null, {
    auth,
    label: "GET /api/recent-chats",
    fixture: () => clone(fixtures.recentChats || []),
  });
  return programId ? result.filter((c) => c.programId === programId) : result;
};

// ─── Notebook ─────────────────────────────────────────────────────────

export const getNotebookForProject = (auth, programId, projectId) =>
  fetchOrFixture(ExternalRESTAPI.ProjectNotebook, { projectId }, {
    auth,
    label: `GET /api/projects/${projectId}/notebook`,
    fixture: () => {
      const nb = fixtures.notebook?.[projectKey(programId, projectId)] || {};
      return {
        findings: clone(nb.findings || []),
        openQuestions: clone(nb.openQuestions || []),
      };
    },
  });

// ─── Data ─────────────────────────────────────────────────────────────

export const getDataForProject = (auth, programId, projectId) =>
  fetchOrFixture(ExternalRESTAPI.ProjectData, { projectId }, {
    auth,
    label: `GET /api/projects/${projectId}/data`,
    fixture: () => {
      const d = fixtures.data?.[projectKey(programId, projectId)] || {};
      return {
        files: clone(d.files || []),
        connectedSources: clone(d.connectedSources || []),
        mockedConnectors: clone(d.mockedConnectors || []),
      };
    },
  });

// ─── Memory ───────────────────────────────────────────────────────────

export const listProjectMemory = (auth, programId, projectId) =>
  fetchOrFixture(ExternalRESTAPI.ProjectMemory, { projectId }, {
    auth,
    label: `GET /api/projects/${projectId}/memory`,
    fixture: () => clone(fixtures.memory?.[projectKey(programId, projectId)] || []),
  });

export const listProgramMemory = (auth, programId) =>
  fetchOrFixture(ExternalRESTAPI.ProgramMemory, { programId }, {
    auth,
    label: `GET /api/programs/${programId}/memory`,
    fixture: () => clone(fixtures.memory?.[programId] || []),
  });

// ─── Invitations ──────────────────────────────────────────────────────

export const listPendingInvitations = (auth, programId) =>
  fetchOrFixture(ExternalRESTAPI.ProgramInvitations, { programId }, {
    auth,
    label: `GET /api/programs/${programId}/invitations`,
    fixture: () => clone(fixtures.teamPendingInvites?.[programId] || []),
  });

// ─── Artifacts (pin / share) ──────────────────────────────────────────

export const listPinnedArtifacts = (auth, programId, projectId) =>
  fetchOrFixture(ExternalRESTAPI.ProjectNotebookPinned, { projectId }, {
    auth,
    label: `GET /api/projects/${projectId}/notebook/pinned`,
    fixture: () => ({ artifacts: [] }),
  });

export const unpinArtifactFromProject = (auth, programId, projectId, artifactId) =>
  fetchOrFixture(`${ExternalRESTAPI.ProjectNotebookPinned}/{artifactId}`, { projectId, artifactId }, {
    auth,
    method: "DELETE",
    label: `DELETE /api/projects/${projectId}/notebook/pinned/${artifactId}`,
    fixture: () => ({ ok: true }),
  });

export const pinArtifactToProject = (auth, programId, projectId, artifact) =>
  fetchOrFixture(ExternalRESTAPI.ProjectNotebookPinned, { projectId }, {
    auth,
    method: "POST",
    body: { artifact, programId },
    label: `POST /api/projects/${projectId}/notebook/pinned`,
    fixture: () => ({
      ok: true,
      pinnedAt: new Date().toISOString(),
      artifact: clone(artifact),
    }),
  });

const sharedArtifacts = new Map();

export const shareArtifact = (auth, artifact) => {
  const artifactId = artifact?.artifactId;
  if (!artifactId) return Promise.reject(new Error("artifact.artifactId required"));
  return fetchOrFixture(ExternalRESTAPI.ArtifactShare, { artifactId }, {
    auth,
    method: "POST",
    body: { artifact },
    label: `POST /api/artifacts/${artifactId}/share`,
    fixture: () => {
      sharedArtifacts.set(artifactId, clone(artifact));
      return { url: `/a/${artifactId}`, artifactId };
    },
  });
};

export const getSharedArtifact = (auth, artifactId) =>
  fetchOrFixture(ExternalRESTAPI.Artifact, { artifactId }, {
    auth,
    label: `GET /api/artifacts/${artifactId}`,
    fixture: () => {
      const a = sharedArtifacts.get(artifactId);
      return a ? clone(a) : null;
    },
  });

// ─── Memory writes (project + program scope) ──────────────────────────

export const addProjectMemory = (auth, projectId, item) =>
  fetchOrFixture(ExternalRESTAPI.ProjectMemory, { projectId }, {
    auth,
    method: "POST",
    body: item,
    label: `POST /api/projects/${projectId}/memory`,
    fixture: () => ({
      id: item?.id || `local-mem-${Date.now().toString(36)}`,
      type: item?.type || "fact",
      scope: "project",
      text: item?.text || "",
      status: "active",
      createdAt: new Date().toISOString(),
    }),
  });

export const addProgramMemory = (auth, programId, item) =>
  fetchOrFixture(ExternalRESTAPI.ProgramMemory, { programId }, {
    auth,
    method: "POST",
    body: item,
    label: `POST /api/programs/${programId}/memory`,
    fixture: () => ({
      id: item?.id || `local-mem-${Date.now().toString(36)}`,
      type: item?.type || "fact",
      scope: "program",
      text: item?.text || "",
      status: "active",
      createdAt: new Date().toISOString(),
    }),
  });

// ─── Notebook writes (manual add) ────────────────────────────────────

export const addFinding = (auth, projectId, finding) =>
  fetchOrFixture(`${ExternalRESTAPI.ProjectNotebook}/findings`, { projectId }, {
    auth,
    method: "POST",
    body: finding,
    label: `POST /api/projects/${projectId}/notebook/findings`,
    fixture: () => ({
      id: finding?.id || `local-f-${Date.now().toString(36)}`,
      text: finding?.text || "",
      source: finding?.source || null,
      createdAt: new Date().toISOString(),
    }),
  });

export const addOpenQuestion = (auth, projectId, question) =>
  fetchOrFixture(`${ExternalRESTAPI.ProjectNotebook}/questions`, { projectId }, {
    auth,
    method: "POST",
    body: question,
    label: `POST /api/projects/${projectId}/notebook/questions`,
    fixture: () => ({
      id: question?.id || `local-q-${Date.now().toString(36)}`,
      text: question?.text || "",
      raisedBy: question?.raisedBy || null,
      createdAt: new Date().toISOString(),
    }),
  });

// ─── Coach-memory manual add (user-scope, real backend) ───────────────

/**
 * POST /api/users/me/coach-memory/items — manually add one memory item
 * to the authenticated user's bank. Backend wraps a Hindsight retain
 * with the appropriate audit/metric pipeline. Fixture: optimistic
 * { operation_id: "" } so the FE can update its local view without
 * waiting on async indexing.
 */
export const addMyCoachMemoryItem = (auth, payload) =>
  fetchOrFixture(ExternalRESTAPI.MyCoachMemoryItems, null, {
    auth,
    method: "POST",
    body: payload,
    label: "POST /api/users/me/coach-memory/items",
    fixture: () => ({ operation_id: "", status: "fixture" }),
  });

// ─── Artifact versions (Canvas Versions menu) ────────────────────────

/**
 * GET /api/artifacts/{artifactId}/versions — version history for the v2
 * artifact detail overlay (PLEX-4222), newest-first. Each entry:
 * `{ version, current, createdAt, author, changeSummary, restoredFrom? }`.
 * A legacy artifact with no snapshots yields a single current v1.
 */
export const listArtifactVersions = (auth, artifactId) =>
  fetchOrFixture(ExternalRESTAPI.ArtifactVersions, { artifactId }, {
    auth,
    method: "GET",
    label: `GET /api/artifacts/${artifactId}/versions`,
    fixture: () => ({
      versions: [{
        version: 1,
        current: true,
        createdAt: new Date().toISOString(),
        author: "you",
        changeSummary: "",
      }],
    }),
  });

/**
 * POST /api/artifacts/{artifactId}/restore — restore an artifact to a prior
 * version (PLEX-4222). Non-destructive: records the restore as a NEW version,
 * so later versions are retained. Returns `{ ok, artifactId, version,
 * restoredFrom }` where `version` is the newly-created current version.
 */
export const restoreArtifact = (auth, artifactId, version) =>
  fetchOrFixture(ExternalRESTAPI.ArtifactRestore, { artifactId }, {
    auth,
    method: "POST",
    body: { version },
    label: `POST /api/artifacts/${artifactId}/restore`,
    fixture: () => ({ ok: true, artifactId, version: 2, restoredFrom: version }),
  });

// ─── Run log (Lineage tab, PLEX-6070) ────────────────────────────────

/**
 * GET /api/projects/{projectId}/runs/{runId} —
 * the full execute_code audit record backing a derived artifact (PLEX-6070):
 * `{ run_id, input_file_ids, output_file_ids, code_file_id, stdout, result,
 * status, error, duration_ms, created_at, ... }`. The Lineage tab already has
 * the capped stdout from the transcript; this returns the immutable, uncapped
 * record. Falls back to null when the endpoint isn't on the cell yet (the tab
 * then shows the transcript's capped stdout).
 */
export const getRunLog = (auth, projectId, runId) =>
  fetchOrFixture(ExternalRESTAPI.RunLog, { projectId, runId }, {
    auth,
    method: "GET",
    label: `GET /api/projects/${projectId}/runs/${runId}`,
    fixture: () => null,
  });

// ─── Upload download URL (attachment chip) ───────────────────────────

/**
 * Returns the absolute URL to GET the cached upload bytes. The endpoint
 * is auth-scoped (Cognito; users only access their own uploads via the
 * per-user S3 prefix) so the FE attaches the bearer via a fetch + blob
 * download pattern rather than a naked <a href> link.
 */
export const uploadDownloadUrl = (uploadId) =>
  fillTemplate(ExternalRESTAPI.UploadDownload || "", { uploadId });

/**
 * Fetch the upload bytes through the authenticated request path, then
 * trigger a browser download. Caller supplies a filename to use for the
 * saved file (the chip's display name).
 */
export const downloadUpload = async (auth, uploadId, filename = null) => {
  const url = uploadDownloadUrl(uploadId);
  if (!url) throw new Error("UploadDownload endpoint not configured");
  const req = new RequestBuilder(url);
  if (auth) req.withAuthorization(auth);
  const response = await req.fetch();
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(`Download failed (${response.status})${detail ? `: ${detail}` : ""}`);
  }
  const blob = await response.blob();
  // Read the suggested filename from Content-Disposition when available;
  // fall back to the caller-provided one, then to the uploadId.
  const cd = response.headers.get("content-disposition") || "";
  const cdMatch = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const suggested = cdMatch ? decodeURIComponent(cdMatch[1]) : null;
  const name = filename || suggested || `${uploadId}.bin`;
  if (typeof window !== "undefined") {
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
  }
  return { ok: true, filename: name };
};

// ─── Team admin (invite / edit-role / remove) ────────────────────────

/**
 * POST /api/programs/{programId}/invitations — owner adds a member to
 * the program. role defaults to "member" on the backend.
 */
export const inviteProgramMember = (auth, programId, payload) =>
  fetchOrFixture(ExternalRESTAPI.ProgramInvitations, { programId }, {
    auth,
    method: "POST",
    body: payload,
    label: `POST /api/programs/${programId}/invitations`,
    fixture: () => {
      const email = (payload?.email || "").trim().toLowerCase();
      const local = email.split("@")[0] || "?";
      const name = local.replace(/[._]/g, " ").trim() || email;
      const initials = name.split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
      return {
        id: email,
        email,
        name,
        initials,
        title: "",
        color: "#7c3aed",
        role: payload?.role || "member",
        lastActiveAt: null,
        invitedAt: new Date().toISOString(),
      };
    },
  });

/**
 * PATCH /api/programs/{programId}/members/{memberEmail} — owner changes
 * a member's role.
 */
export const updateProgramMemberRole = (auth, programId, memberEmail, role) =>
  fetchOrFixture(ExternalRESTAPI.ProgramMember, { programId, memberEmail }, {
    auth,
    method: "PATCH",
    body: { role },
    label: `PATCH /api/programs/${programId}/members/${memberEmail}`,
    fixture: () => ({ email: memberEmail, role }),
  });

/**
 * DELETE /api/programs/{programId}/members/{memberEmail} — owner removes
 * a member from the program.
 */
export const removeProgramMember = (auth, programId, memberEmail) =>
  fetchOrFixture(ExternalRESTAPI.ProgramMember, { programId, memberEmail }, {
    auth,
    method: "DELETE",
    label: `DELETE /api/programs/${programId}/members/${memberEmail}`,
    fixture: () => ({ ok: true }),
  });

// ─── Project access (grant / revoke — PLEX-4125; backend PLEX-4124) ───
//
// Distinct from the program invite/remove endpoints above: these grant or
// revoke access to a *single project* without touching the parent program's
// membership. A "project grant" makes a non-program-member a member of just
// this project. Binary add/remove by email — no role tier (project-level
// role labels are cosmetic per the v2 access-control locked decisions).

/**
 * POST /api/projects/{projectId}/access — owner-or-admin grants project-only
 * access to a user by email. Backend dual-writes: appends the project member
 * list, writes the grantee's V2ACCESS index row, and an audit row. Idempotent.
 */
export const grantProjectAccess = (auth, projectId, email) =>
  fetchOrFixture(ExternalRESTAPI.ProjectAccess, { projectId }, {
    auth,
    method: "POST",
    body: { email },
    label: `POST /api/projects/${projectId}/access`,
    fixture: () => {
      const addr = (email || "").trim().toLowerCase();
      const local = addr.split("@")[0] || "?";
      const name = local.replace(/[._]/g, " ").trim() || addr;
      const initials = name.split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
      return {
        id: addr,
        email: addr,
        name,
        initials,
        title: "",
        color: "#7c3aed",
        provenance: "project",
        lastActiveAt: null,
        grantedAt: new Date().toISOString(),
      };
    },
  });

/**
 * DELETE /api/projects/{projectId}/access/{memberEmail} — owner-or-admin
 * revokes a user's project-level access. Path-param addressing mirrors the
 * program-member DELETE. Backend dual-deletes: removes the member from the
 * project member list, deletes the V2ACCESS row, writes an audit row. The
 * parent program's membership is untouched.
 */
export const revokeProjectAccess = (auth, projectId, memberEmail) =>
  fetchOrFixture(ExternalRESTAPI.ProjectAccessMember, { projectId, memberEmail }, {
    auth,
    method: "DELETE",
    label: `DELETE /api/projects/${projectId}/access/${memberEmail}`,
    fixture: () => ({ ok: true }),
  });
